import {
  AccessCredential,
  AuthTokenResponse,
  RefreshCredential,
  WorkspaceContext,
  WorkspaceContextType,
} from '../types/authTypes';

export function parseJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid JWT format');
  }
  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    '='
  );
  const decoded = atob(padded);
  return JSON.parse(decoded) as Record<string, unknown>;
}

export function isWorkspaceScopedToken(token: string): boolean {
  try {
    const payload = parseJwtPayload(token);
    const tenantId = payload['tenant_id'];
    return typeof tenantId === 'string' && tenantId.length > 0;
  } catch {
    return false;
  }
}

export function extractWorkspaceClaimsFromToken(
  token: string
): Partial<WorkspaceContext> {
  const payload = parseJwtPayload(token);

  const rawRoles = payload['workspace_role'];
  let roles: string[] = [];
  if (Array.isArray(rawRoles)) {
    roles = rawRoles as string[];
  } else if (typeof rawRoles === 'string') {
    roles = [rawRoles];
  }

  return {
    tenantId: (payload['tenant_id'] as string) || undefined,
    tenantName: (payload['tenant_name'] as string) || undefined,
    companyId: (payload['company_id'] as string) || undefined,
    companyName: (payload['company_name'] as string) || undefined,
    contextType:
      (payload['workspace_context_type'] as WorkspaceContextType) || undefined,
    roles,
    membershipId: (payload['membership_id'] as string) || '',
    workspaceSessionId: (payload['workspace_session_id'] as string) || '',
  };
}

export function mapTokenResponseToCredentials(
  response: AuthTokenResponse,
  issuedAt: Date
): { access: AccessCredential; refresh: RefreshCredential | null } {
  const issuedAtIso = issuedAt.toISOString();
  const expiresAt = new Date(
    issuedAt.getTime() + response.expiresIn * 1000
  ).toISOString();
  const scopes = response.scope ? response.scope.split(' ') : [];

  let jwtClaims: Partial<AccessCredential> = {};
  try {
    const payload = parseJwtPayload(response.accessToken);
    jwtClaims = {
      tenantId: (payload['tenant_id'] as string) || undefined,
      companyId: (payload['company_id'] as string) || undefined,
      membershipId: (payload['membership_id'] as string) || undefined,
      workspaceSessionId:
        (payload['workspace_session_id'] as string) || undefined,
    };

    const rawRoles = payload['workspace_role'];
    if (Array.isArray(rawRoles)) {
      jwtClaims.workspaceRoles = rawRoles as string[];
    } else if (typeof rawRoles === 'string') {
      jwtClaims.workspaceRoles = [rawRoles];
    }
  } catch {
    // identity-only token — no workspace claims
  }

  const access: AccessCredential = {
    tokenType: 'Bearer',
    value: response.accessToken,
    issuedAt: issuedAtIso,
    expiresAt,
    scopes,
    ...jwtClaims,
  };

  const refresh: RefreshCredential | null = response.refreshToken
    ? {
        value: response.refreshToken,
        issuedAt: issuedAtIso,
        status: 'active',
      }
    : null;

  return { access, refresh };
}
